/**
 * Assignment-only deal fields — the deal-level columns that only mean
 * something when Warwick is taking an assignment of an existing lease.
 *
 * The assignment dates / term describe the assignment instrument itself, and
 * the three economics columns are struck on the assignment (`delivered_nri` is
 * the NRI the assignor delivers; `bonus_per_acre` / `total_bonus` are what
 * Warwick pays for it). On a LEASE / DEED / OPTION / SURFACE deal there is no
 * assignment to date and no assigned NRI to price, so the Deal tab drops the
 * rows entirely rather than showing eight permanently-blank fields.
 *
 * Judged on `deals.deal_type` alone — the pick list is LEASE / ASSIGNMENT /
 * DEED / OPTION / SURFACE (migration 051). Note this is deliberately NARROWER
 * than `isRecordingsApplicable`, which also admits a DIVESTITURE
 * `deal_category`: a recording exists for any assignment instrument filed with
 * the county, in or out, but these columns are the acquisition-side terms.
 *
 * Dropping the metadata row (rather than hiding it in the view only) is what
 * keeps the field out of BOTH renderings and out of the PUT body:
 * `serializeDealForm` skips any form value with no metadata row, so a stale
 * value on a mistyped deal is never echoed back on save.
 */
const norm = (v) => String(v ?? "").trim().toUpperCase();

export const ASSIGNMENT_ONLY_FIELDS = new Set([
  "assignment_date",
  "assignment_effective_date",
  "assignment_term_length",
  "assignment_term_units",
  "assignment_expiration_date",
  "delivered_nri",
  "bonus_per_acre",
  "total_bonus",
]);

export const isAssignmentDeal = (deal) => norm(deal?.deal_type) === "ASSIGNMENT";

/**
 * Drop the assignment-only metadata rows unless this deal is an assignment.
 *
 * @param {Array<object>} metadata — UIMetadata rows for `deals`
 * @param {object} deal — the deal row (read for `deal_type`)
 * @returns {Array<object>} metadata, minus the non-applicable rows
 */
export const filterAssignmentOnlyFields = (metadata, deal) => {
  const rows = Array.isArray(metadata) ? metadata : [];
  if (isAssignmentDeal(deal)) return rows;
  return rows.filter((m) => !ASSIGNMENT_ONLY_FIELDS.has(m?.field));
};

export default filterAssignmentOnlyFields;
